import { use, useEffect } from 'react';

import { TinkerableContext } from '../TinkerableContext';
import { scrollToId } from '../scrollToId';

/**
 * Deep-linking Capability C (MARKDOWN_SYNTAX_SPEC §13.5): after an in-app navigation
 * whose URL carries a `#fragment`, scroll the target section into view.
 *
 * In-app navigation swaps the rendered file **asynchronously** — the destination
 * file's tree mounts *after* the route change — so the element the fragment
 * addresses does not exist at click time. This effect records the pending fragment
 * and retries until the target appears: an immediate attempt, a `MutationObserver`
 * over late-mounting subtrees, and a few timed retries (the `[120, 300, 600]ms`
 * cadence `grove/src/components/Toc.tsx` proves for late-mounting prose). If the
 * target never appears within the window it degrades to top-of-page — a missing
 * fragment is never a hard failure.
 *
 * It re-runs when the destination page **or** the fragment changes, so a fresh click
 * on the same target re-scrolls. Mounted once inside the navigation provider (see
 * `boot`'s `TinkerableApp`) so it is uniform for **every** MDX app — the SDK router
 * owns cross-page anchor navigation, not any one consumer (Grove).
 */
export const useScrollAfterNavigation = (): void => {
  const { navigationState } = use(TinkerableContext);
  const frag = navigationState.hash;
  // Re-run when the destination page OR the fragment changes. The two are joined
  // with a NUL — a byte neither a sandbox path nor a fragment can contain — so no
  // pair of (path, fragment) values can collide into the same key. It is written as
  // the \u0000 ESCAPE, never as a raw byte: a literal NUL in the source makes the
  // file binary to grep, diff and review tooling.
  const navKey = `${navigationState.sandboxPath}\u0000${frag}`;

  useEffect(() => {
    if (!frag || typeof document === 'undefined') return;
    // Fast path: the target is already in the DOM (same page, or the tree mounted
    // synchronously) — scroll now, no observer/timer churn.
    if (scrollToId(frag)) return;

    let done = false;
    const finish = () => {
      done = true;
      observer.disconnect();
      timers.forEach(clearTimeout);
      clearTimeout(finalTimer);
    };
    const tryScroll = () => {
      if (!done && scrollToId(frag)) finish();
    };

    const observer = new MutationObserver(tryScroll);
    const timers = [120, 300, 600].map((ms) => setTimeout(tryScroll, ms));
    // Final fallback once the retry window closes: if the fragment never resolved,
    // scroll to the top rather than strand the reader at the previous page's offset.
    const finalTimer = setTimeout(() => {
      if (!done) {
        finish();
        window.scrollTo?.(0, 0);
      }
    }, 900);

    observer.observe(document.body, { childList: true, subtree: true });
    return finish;
  }, [navKey, frag]);
};

/** Null-rendering mount point for {@link useScrollAfterNavigation} inside the
 *  navigation provider. */
export const ScrollAfterNavigation = (): null => {
  useScrollAfterNavigation();
  return null;
};
